import { getNextFlowPageIdAfterPayment } from 'nove8/helpers/getNextFlowPageIdAfterPayment'
import {
  IRequestOptions,
  IRequestParams,
  IResponseResult,
  TResponseErrorData,
} from 'models/api.model'
import { goTo } from 'route-history'

export class ApiService {
  private readonly urlPrefix: string
  private readonly initialHeaders: IRequestOptions['headers'] | null

  constructor(url?: string, headers?: IRequestOptions['headers']) {
    this.urlPrefix = url || ''
    this.initialHeaders = headers || null
  }

  public async makeRequest<T>(
    url: string,
    options: IRequestOptions,
  ): Promise<IResponseResult<T>> {
    const requestURL = this.buildURL(url, options.params)
    return this.request<T>(requestURL, options)
  }

  public async makeCustomRequest<T>(
    url: string,
    options: IRequestOptions,
  ): Promise<IResponseResult<T>> {
    const requestURL = this.buildURLWithoutPrefix(url, options.params)
    return this.request<T>(requestURL, options)
  }

  public async makeCustomRequestWithoutScheme<T>(
    url: string,
    options: IRequestOptions,
  ): Promise<IResponseResult<T>> {
    const requestURL = this.buildURLWithoutScheme(url, options.params)
    return this.request<T>(requestURL, options)
  }

  public getDefaultHeaders(isFormData: boolean): Headers {
    // When using FormData to submit POST requests using XMLHttpRequest
    // or the Fetch API with the multipart/form-data content type
    // (e.g., when uploading files and blobs to the server),
    // do not explicitly set the Content-Type header on the request.
    // Doing so will prevent the browser from being able to set
    // the Content-Type header with the boundary expression it will use
    // to delimit form fields in the request body
    return new Headers({
      ...(isFormData
        ? {}
        : { Accept: 'application/json', 'Content-Type': 'application/json' }),
      ...Object.fromEntries(this.initialHeaders?.entries() || []),
    })
  }

  protected async request<T>(
    url: string,
    options: IRequestOptions,
  ): Promise<IResponseResult<T>> {
    try {
      const isFormData = !!(options.body instanceof FormData)
      const requestBody =
        options.body instanceof FormData
          ? options.body
          : options.body
          ? JSON.stringify(options.body)
          : undefined

      const res = await fetch(url, {
        method: options.method,
        headers: options.headers
          ? {
              ...Object.fromEntries(
                this.getDefaultHeaders(isFormData).entries(),
              ),
              ...Object.fromEntries(options.headers.entries()),
            }
          : this.getDefaultHeaders(isFormData),
        body: requestBody,
        credentials: options.credentials || 'include',
        ...(options.keepalive !== undefined && {
          keepalive: options.keepalive,
        }),
        ...(options.mode !== undefined && {
          mode: options.mode,
        }),
        ...(options.signal !== undefined && {
          signal: options.signal,
        }),
      })

      if (!res.ok) {
        return this.processError(res)
      }

      return this.processResponse<T>(res)
    } catch (error: any) {
      return this.processError(error)
    }
  }

  protected buildQuery(params?: IRequestParams): string {
    let query = ''

    if (params) {
      const items: string[] = []
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((valueItem) =>
            items.push(
              `${encodeURIComponent(key)}=${encodeURIComponent(valueItem)}`,
            ),
          )
        } else {
          items.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        }
      })
      query = `?${items.join('&')}`
    }

    return query
  }

  protected buildURL(url: string, params?: IRequestParams): string {
    const query: string = this.buildQuery(params)
    return `${this.urlPrefix}/${url}${query}`
  }

  protected buildURLWithoutPrefix(
    url: string,
    params?: IRequestParams,
  ): string {
    const query: string = this.buildQuery(params)
    return `https://${url}${query}`
  }

  protected buildURLWithoutScheme(
    url: string,
    params?: IRequestParams,
  ): string {
    const query: string = this.buildQuery(params)
    return `${url}${query}`
  }

  protected async processResponse<T>(
    response: Response,
  ): Promise<IResponseResult<T>> {
    try {
      const result: T = await response.json()

      return {
        success: true,
        status: response.status,
        data: result,
      }
    } catch {
      return {
        success: true,
        status: response.status,
      }
    }
  }

  protected async processError(
    error: Response | Error | string,
  ): Promise<IResponseResult<TResponseErrorData>> {
    const isErrorTypeResponse = error instanceof Response
    let bodyJson = null

    if (isErrorTypeResponse) {
      bodyJson = await error.json()
    }

    if (isErrorTypeResponse && error.status === 401) {
      goTo(`/${getNextFlowPageIdAfterPayment()}`)
    }

    const data: TResponseErrorData = isErrorTypeResponse ? bodyJson : error

    return Promise.resolve<IResponseResult<TResponseErrorData>>({
      success: false,
      status: isErrorTypeResponse ? error.status : undefined,
      data,
    })
  }
}
